import { type KeyboardEvent } from 'react'
import { MoreHorizontal, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import type { LexProduct } from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

type ProductCatalogCardProps = {
  product: LexProduct
  onDeleteClick: (product: LexProduct) => void
}

export function ProductCatalogCard({
  product,
  onDeleteClick,
}: ProductCatalogCardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation(['productCatalog', 'common'])

  const title = product.name?.trim() || t('productCatalog:fallbackProductTitle')
  const productDetailPath = `/product-catalog/${product.id}`

  function handleCardActivate() {
    navigate(productDetailPath)
  }

  function handleCardKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleCardActivate()
    }
  }

  return (
    <Card
      role='link'
      tabIndex={0}
      aria-label={title}
      className='cursor-pointer transition-colors hover:border-primary/50'
      onClick={handleCardActivate}
      onKeyDown={handleCardKeyDown}
    >
      <CardHeader className='flex flex-row items-center justify-between gap-4 space-y-0'>
        <CardTitle className='min-w-0 flex-1 text-base'>{title}</CardTitle>
        <div onClick={(event) => event.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0'
                aria-label={t('productCatalog:list.card.actionsMenuAriaLabel', {
                  title,
                })}
              >
                <MoreHorizontal className='size-4' aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                variant='destructive'
                onClick={() => onDeleteClick(product)}
              >
                <Trash2 className='size-4' aria-hidden />
                {t('common:actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
    </Card>
  )
}
